/* eslint-disable no-underscore-dangle,no-param-reassign */
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import i18n from '@i18next/i18n';
import axios from 'axios';
// eslint-disable-next-line no-unused-vars
import { IDownloadManager } from './download-manager.interface';
import handlePcExport from './export-utils';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';

const POLLING_TIMEOUT = 60 * 1000;
const SUCCESS_CODE = 0;

interface ImageInfo {
    name: string;
    url: string;
}

class DownloadManager implements IDownloadManager {
    private timerId: NodeJS.Timer|null;
    private loading;
    public downloadImg(src: string) {
        if (!src) {
            // eslint-disable-next-line @tencent/i18next/no-literal-string
            window?.log?.info('图片下载 src为空');
            return;
        }

        if (!window.navigator.onLine) {
            this.showNetWorkError();
            return;
        }
        this.download(src);
    }

    public async downloadMutiImageToZip(imagesInfo: ImageInfo[]) {
        const { padId, domainId } = SlideDataServices.DocInfoManager() ?? {};
        try {
            // FIXME: 没有给slide用的接口，用sheet的凑合一下，后台:bruceyyang/haiyangli
            const { data } = await axios.post(
                `/v1/export/sheet/image/${padId}`,
                imagesInfo,
                {
                    params: {
                        domainId,
                    },
                },
            );
            this.startQuery(data.operationId);
        } catch {
            this.stopQuery();
            this.showNetWorkError();
        }
    }

    /**
     * 从图片类型中获取图片后缀
     * @param {String} type 图片请求类型，例如：image/svg+xml image/jpeg
     * @param {String} 图片后缀，例如：.svg .jpeg
     */
    public getTypeToSuffix(type) {
        // eslint-disable-next-line prefer-destructuring
        const imageType = type.split('/')[1];
        const suffix = imageType.indexOf('+') === -1 ? imageType : imageType.split('+')[0];
        return `.${suffix}`;
    }

    public showNetWorkError() {
        if (browser.isMobile) {
            import('@tencent/dui-mobile/lib/components/Snackbar').then((obj) => {
                const DUIMobileSnackbar = obj.default;
                DUIMobileSnackbar.show({ message: i18n.t('下载失败请稍后再试。'), duration: 3000 });
            })
                .catch((error) => {
                    window.log.error(`[loadChunk-error]${error}`);
                });
        } else {
            import('@tencent/dui/lib/components/Snackbar').then((obj) => {
                const DUISnackbar = obj.default;
                DUISnackbar.show({ message: i18n.t('下载失败请稍后再试。'), duration: 3000 });
            })
                .catch((error) => {
                    window.log.error(`[loadChunk-error]${error}`);
                });
        }
    }

    public showRequestTimeout() {
        if (browser.isMobile) {
            import('@tencent/dui-mobile/lib/components/Snackbar').then((obj) => {
                const DUIMobileSnackbar = obj.default;
                DUIMobileSnackbar.show({ message: i18n.t('请求超时'), duration: 3000 });
            })
                .catch((error) => {
                    window.log.error(`[loadChunk-error]${error}`);
                });
        } else {
            import('@tencent/dui/lib/components/Snackbar').then((obj) => {
                const DUISnackbar = obj.default;
                DUISnackbar.show({ message: i18n.t('请求超时'), duration: 3000 });
            })
                .catch((error) => {
                    window.log.error(`[loadChunk-error]${error}`);
                });
        }
    }

    public downloadVideo(objKey) {
        axios('/v2/file/attachment/detail', {
            headers: {
                'Content-Type': 'application/json; charset=UTF-8',
            },
            method: 'POST',
            data: {
                access_type: 0,  // 固定参数
                obj_keys: [objKey],
            },
        }).then((res) => {
            this.downloadVideoResolve(res);
        })
            .catch(() => {
                this.showNetWorkError();
            });
    }

    private downloadVideoResolve(res) {
        const { data } = res;
        if (data?.msg === 'success') {
            const { detail } = data.result;
            if (Array.isArray(detail) && detail[0]) {
                // eslint-disable-next-line prefer-destructuring
                const { downloadUrl, name, ext } = detail[0];
                this.download(downloadUrl, name, ext);
            }
        } else {
            this.showNetWorkError();
        }
    }

    private download(src: string, fileName?: string, ext?: string) {
        if (!src) {
            return;
        }
        let retryCount = 0;

        const xhr = new XMLHttpRequest();
        xhr.responseType = 'blob';
        xhr.onload = () => {
            this.xhrOnloadAfterSendForDownload(xhr, fileName, ext);
        };
        xhr.onerror = () => {
            retryCount = this.xhrOnerrorAfterSendForDownload(xhr, src, retryCount);
        };

        xhr.open('GET', src, true);
        xhr.send();
    }

    private xhrOnloadAfterSendForDownload(xhr, fileName?: string, ext?: string) {
        if ((xhr.status === 200 || xhr.status === 304) && xhr.response) {
            const blobLocalPath = (window.URL ? window.URL : window.webkitURL).createObjectURL(xhr.response);
            const aHtml = document.createElement('a');
            document.body.appendChild(aHtml);
            aHtml.style.display = 'none';
            aHtml.href = blobLocalPath;
            const suffix = ext ? `.${ext}` : this.getTypeToSuffix(xhr.response.type);
            this.getTypeToSuffix(xhr.response.type);
            this.checkBlobLocalPathForDownload(blobLocalPath, aHtml, suffix, fileName);
            aHtml.click();
            // 异步确保移出，避免内存泄露
            setTimeout(() => {
                document.body.removeChild(aHtml);
                (window.URL ? window.URL : window.webkitURL).revokeObjectURL(blobLocalPath);
            }, 1000);
        } else {
            this.showNetWorkError();
        }
    }

    private checkBlobLocalPathForDownload(blobLocalPath: string, aHtml, suffix: string, fileName?: string) {
        if (blobLocalPath) {
            const tempArray = blobLocalPath.split('/');
            if (fileName) {
                aHtml.download = fileName + suffix;
            } else if (tempArray.length > 1) {
                aHtml.download = tempArray.slice(-1) + suffix;
            } else {
                aHtml.download = `${i18n.t('未命名')}${suffix}`;
            }
        }
    }

    private xhrOnerrorAfterSendForDownload(xhr, src: string, retryCount: number,  backupUrl?: string) {
        xhr.open('GET', backupUrl || src, true);
        // 失败重新一次
        if (retryCount === 0) {
            xhr.send();
            retryCount += 1;
        } else {
            this.showNetWorkError();
        }
        return retryCount;
    }

    // 开始轮询
    private startQuery(operationId: string) {
        this.showLoading();
        const startPollingTime = Date.now();
        this.timerId = setInterval(async () => {
            if (Date.now() - startPollingTime > POLLING_TIMEOUT) {
                // 轮询超时
                this.stopQuery();
                this.showRequestTimeout();
                return;
            }

            try {
                const { padId } = SlideDataServices.DocInfoManager() ?? {};
                const { data } = await axios.get(
                    `/v1/export/sheet/image/progress/${padId}`,
                    { params: { operationId } },
                );

                if (!this.checkCgiResult(data)) {
                    return;
                }

                if (data.url) {
                    this.stopQuery();
                    handlePcExport(data.url);
                }
            } catch (err) {
                this.stopQuery();
                this.showNetWorkError();
            }
            // 没有url，后台还没打包好，接着轮询
        }, 1 * 1000);
    }

    private stopQuery() {
        this.closeLoading();
        if (this.timerId) {
            clearInterval(this.timerId);
            this.timerId = null;
        }
    }

    private checkCgiResult(data) {
        if (data.ret !== SUCCESS_CODE) {
            this.stopQuery();
            return false;
        }
        return true;
    }

    private showLoading() {
        import('@tencent/dui/lib/components/Loading').then((obj) => {
            this.loading = obj.default.show({ tip: i18n.t('正在下载，请稍候...') });
        })
            .catch((error) => {
                window.log.error(`[loadChunk-error]${error}`);
            });
    }

    private closeLoading() {
        this.loading?.close();
    }
}
const downloadManager = new DownloadManager();

export default downloadManager;
